/* eslint-disable @typescript-eslint/camelcase */
import { Grid, makeStyles } from '@material-ui/core';
import { AccessAlarm, NotificationsActive, Subject } from '@material-ui/icons';
import clsx from 'clsx';
import DatePicker from 'components/DatePicker';
import TextEditor from 'components/TextEditor';
import ToggleCard from 'components/ToggleCard';
import i18n from 'i18n';
import {
  CompanyData,
  PermissionsData,
  RolesEnum,
} from 'modules/authenticate/interfaces';
import React from 'react';
import GenericForm from 'skyone-lib/lib/GenericForm';
import { inAdminView, inOperatorView, inSuperAdminView } from 'utils/role';
import { parseISO, isValid, isAfter, isFuture } from 'date-fns';
import styles from '../styles';

const useStyles = makeStyles(styles);

export interface MaintenanceDisplay {
  active: boolean;
  start_date: string | null;
  end_date: string | null;
  message: any;
  display_to_logged_users: boolean;
}

export const displayInitialValues = {
  active: false,
  start_date: null,
  end_date: null,
  message: '',
  display_to_logged_users: false,
};

export interface Props {
  defaultValue: MaintenanceDisplay | null;
  companySelected: CompanyData | null;
  role?: RolesEnum;
  permissions: PermissionsData | undefined;
  onChange: (values: MaintenanceDisplay) => void;
  setHasError: React.Dispatch<React.SetStateAction<boolean>>;
}

const Form = ({
  defaultValue,
  role,
  companySelected,
  permissions,
  onChange,
  setHasError,
}: Props) => {
  const classes = useStyles();
  const [
    startDatetimeValue,
    setStartDatetimeValue,
  ] = React.useState<Date | null>(
    defaultValue?.start_date ? parseISO(defaultValue.start_date) : null
  );
  const [endDatetimeValue, setEndDatetimeValue] = React.useState<Date | null>(
    defaultValue?.end_date ? parseISO(defaultValue.end_date) : null
  );
  const [isStartDatetimeValid, setIsStartDatetimeValid] = React.useState(true);
  const [isEndDatetimeValid, setIsEndDatetimeValid] = React.useState(true);

  const [values, setValues] = React.useState<MaintenanceDisplay>(
    displayInitialValues
  );

  const handleChange = (
    values: MaintenanceDisplay,
    toggle: boolean,
    shouldChangeDefaultValues = true
  ) => {
    const newValues = { ...values, active: toggle };

    setValues(newValues);
    // eslint-disable-next-line no-unused-expressions
    shouldChangeDefaultValues && onChange(newValues);
  };

  React.useEffect(() => {
    if (defaultValue) {
      setValues(defaultValue);
    }
  }, [defaultValue]);

  React.useEffect(() => {
    setIsStartDatetimeValid(
      !!startDatetimeValue &&
        isValid(startDatetimeValue) &&
        isFuture(startDatetimeValue)
    );
    setIsEndDatetimeValid(
      !!endDatetimeValue &&
        !!startDatetimeValue &&
        isValid(endDatetimeValue) &&
        isAfter(endDatetimeValue, startDatetimeValue)
    );
  }, [startDatetimeValue, endDatetimeValue]);

  React.useEffect(() => {
    if (!values?.active) {
      setHasError(false);
    } else {
      setHasError(!isStartDatetimeValid || !isEndDatetimeValid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStartDatetimeValid, isEndDatetimeValid, values]);

  return (
    <ToggleCard
      active={values.active}
      id="toggle-show-alert"
      title={i18n.t('SHOW_ALERT_KEY')}
      disable={!!permissions && !permissions?.can_edit_env_scheduled_warnings}
      getActive={(value: boolean) => handleChange(values, !value)}
      icon={
        <NotificationsActive
          className={clsx({
            [classes.iconGreen]: inAdminView(role, companySelected),
            [classes.iconGray]: inSuperAdminView(role, companySelected),
            [classes.iconBlue]: inOperatorView(role, companySelected),
            [classes.iconYellow]: companySelected,
          })}
        />
      }
    >
      <Grid container className={classes.FormContainer}>
        <AccessAlarm className={classes.iconMaintenance} />
        <Grid item className={classes.datePicker}>
          <DatePicker
            id="display-start_date"
            disabled={
              !!permissions && !permissions?.can_edit_env_scheduled_warnings
            }
            type="datetime"
            label={i18n.t('START_ALERT_KEY')}
            disablePast
            minDate={new Date()}
            keyValue="start_date"
            value={values?.start_date}
            error={!isStartDatetimeValid}
            setFieldsValue={(key, value) => {
              handleChange(
                { ...values, [key]: value, end_date: null },
                values.active
              );
              setStartDatetimeValue(
                typeof value === 'string' ? parseISO(value) : (value as Date)
              );
            }}
          />
        </Grid>
        <Grid item className={classes.datePicker}>
          <DatePicker
            id="display-end_date"
            disabled={
              !!permissions && !permissions?.can_edit_env_scheduled_warnings
            }
            type="datetime"
            label={i18n.t('END_ALERT_KEY')}
            value={values?.end_date}
            disablePast
            error={!isEndDatetimeValid}
            minDate={startDatetimeValue}
            keyValue="end_date"
            setFieldsValue={(key, value) => {
              handleChange({ ...values, [key]: value }, values.active);
              setEndDatetimeValue(
                typeof value === 'string' ? parseISO(value) : (value as Date)
              );
            }}
          />
        </Grid>
        <GenericForm
          overrideClasses={{ checkboxLabel: { fontSize: 12, fontWeight: 500 } }}
          errors={[]}
          values={values}
          setFieldValue={(key, value) =>
            handleChange({ ...values, [key]: value }, values.active)
          }
          screenFields={[
            {
              gridXs: 12,
              label: i18n.t('DISPLAY_TO_LOGGED_USERS_KEY'),
              stateKey: 'display_to_logged_users',
              isCheckbox: true,
              disabled:
                !!permissions && !permissions?.can_edit_env_scheduled_warnings,
            },
          ]}
        />

        <Grid item className={classes.textFieldContainer}>
          <Subject className={classes.subject} />
          <TextEditor
            initialValue={values.message}
            title={i18n.t('ADD_MESSAGE_MAINTENANCE_KEY')}
            getValue={value =>
              handleChange({ ...values, message: value }, values.active)
            }
            maxLength={800}
            readOnly={
              !!permissions && !permissions?.can_edit_env_scheduled_warnings
            }
          />
        </Grid>
      </Grid>
    </ToggleCard>
  );
};

export default Form;
